'use client';

import classNames from 'classnames';
import type { ApprovalDegree } from '../../types/approvalOdds';
import { getApprovalOddsStatus } from '../../utils/approvalOdds';
import { MaterialIcon } from '../MaterialIcon';
import Tooltip from '../Tooltip';
import type { ProductCardApprovalOddsLabels, ProductCardProps } from './types';

type SectionApprovalOddsStaticText = {
  refresh: string;
};

type Props = Pick<
  ProductCardProps,
  | 'approvalOdds'
  | 'approvalOddsState'
  | 'approvalOddsLabels'
  | 'onApprovalOddsUnlockClick'
  | 'onApprovalOddsRefreshClick'
> & {
  staticText: SectionApprovalOddsStaticText;
};

type KnownDegree = Exclude<ApprovalDegree, 'UNKNOWN'>;

const ROW_BG: Record<KnownDegree, string> = {
  HIGH: 'bg-highlights/green-light',
  MEDIUM: 'bg-highlights/orange-light',
  LOW: 'bg-highlights/red-light',
};

const DEGREE_TEXT: Record<KnownDegree, string> = {
  HIGH: 'text-highlights/green-dark',
  MEDIUM: 'text-highlights/yellow-dark',
  LOW: 'text-highlights/red-dark',
};

const DEGREE_DOT: Record<KnownDegree, string> = {
  HIGH: 'bg-highlights/green-dark',
  MEDIUM: 'bg-highlights/yellow-dark',
  LOW: 'bg-highlights/red-dark',
};

const DEGREE_LABEL_KEYS: Record<
  KnownDegree,
  keyof ProductCardApprovalOddsLabels
> = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const SectionApprovalOdds: React.FC<Props> = ({
  approvalOdds,
  approvalOddsState,
  approvalOddsLabels,
  onApprovalOddsUnlockClick,
  onApprovalOddsRefreshClick,
  staticText,
}) => {
  const { isLocked, isFresh } = getApprovalOddsStatus(approvalOddsState);

  if (!isFresh && !isLocked && !onApprovalOddsRefreshClick) {
    console.error(
      'SectionApprovalOdds: onApprovalOddsRefreshClick is required when approval odds are not fresh and not locked.',
    );
  }

  if (!approvalOddsLabels) {
    return null;
  }

  const degree: KnownDegree | null =
    approvalOdds && approvalOdds !== 'UNKNOWN' ? approvalOdds : null;

  const rowBg = degree ? ROW_BG[degree] : 'bg-fill/alternative-1';

  return (
    <div
      className={classNames(
        'my-3 flex items-center justify-between gap-spacing/gap/soft-8px rounded-radius/tooltip p-spacing/padding/soft-12px',
        rowBg,
      )}
    >
      <div
        className={classNames('flex items-center gap-spacing/gap/soft-4px', {
          'opacity-50': !isFresh,
        })}
      >
        <MaterialIcon
          icon="approval_delegation"
          px={18}
          className="text-text/primary"
          aria-hidden
        />
        <span className="text-text/primary mhg-alpha-typography-bullet-1">
          {approvalOddsLabels.sectionTitle}
        </span>
        <Tooltip
          content={approvalOddsLabels.tooltip}
          onOpenEvent="hover"
          tooltipPaddingClass="px-spacing/padding/x-sm py-spacing/padding/soft-12px"
        >
          <MaterialIcon
            icon="info"
            // eslint-disable-next-line tailwindcss/no-arbitrary-value -- inherited from main (ApprovalOdds); not in scope of CMS-1747
            className="cursor-pointer text-text/secondary-lighter text-[16px] md:text-[18px]"
            aria-label={approvalOddsLabels.tooltip}
            onClick={() =>
              isLocked
                ? onApprovalOddsUnlockClick?.()
                : onApprovalOddsRefreshClick?.()
            }
          />
        </Tooltip>
      </div>

      {isLocked ? (
        <button
          type="button"
          onClick={onApprovalOddsUnlockClick}
          className="flex items-center gap-spacing/gap/soft-4px text-text/primary mhg-alpha-typography-bullet-1"
        >
          <MaterialIcon icon="lock" px={16} aria-hidden />
          <span>{approvalOddsLabels.unlock}</span>
        </button>
      ) : (
        <div className="flex items-center gap-spacing/gap/soft-12px">
          {!isFresh && Boolean(onApprovalOddsRefreshClick) && (
            <button
              className="self-stretch inline-flex justify-start items-center gap-x-spacing/gap/soft-6px"
              type="button"
              onClick={onApprovalOddsRefreshClick}
            >
              <MaterialIcon icon="refresh" px={16} aria-hidden />
              <div className="justify-start text-text/primary text-base font-semibold leading-5">
                {staticText.refresh}
              </div>
            </button>
          )}

          {degree ? (
            <div
              className={classNames(
                'flex items-center gap-spacing/gap/soft-4px',
                { 'opacity-50': !isFresh },
              )}
            >
              <span
                className={classNames(
                  'inline-block size-2.5 rounded-full',
                  DEGREE_DOT[degree],
                )}
              />
              <span
                className={classNames(
                  'mhg-alpha-typography-bullet-1 font-font-weight/title-2',
                  DEGREE_TEXT[degree],
                )}
              >
                {approvalOddsLabels[DEGREE_LABEL_KEYS[degree]]}
              </span>
            </div>
          ) : (
            <div
              className={classNames('text-text/secondary-lighter', {
                'opacity-50': !isFresh,
              })}
            >
              -
            </div>
          )}
        </div>
      )}
    </div>
  );
};
